import { type Action, createActionsHook, createHook, createStore } from 'react-sweet-state';

import { type UserSearchItem } from '@atlaskit/smart-common';
import { type AutoSuggestionResult } from '@atlassian/search-client/auto-suggestion';

import { type SearchLinkProps } from '../../../common/ui/search-link/types';
import { type SearchResultCount } from '../../../common/utils/events/dropdown-results-shown';

type PeopleAndTeamsResults = {
	results: UserSearchItem[];
	duration: number;
};

type AutoSuggestionsResults = {
	results: AutoSuggestionResult[];
	duration: number;
};

export type SelectedInputFilter = {
	id: string;
	displayText: string;
};

export type DropdownState = {
	showDropdown: boolean;
	dropdownLoading: boolean;
	activeQueryFilterId: string;
	prefetchedRecentViewsResults: SearchLinkProps[] | undefined;
	prefetchedRecentSearchResults: SearchLinkProps[];
	peopleAndTeamsResults: PeopleAndTeamsResults;
	recentViewsResults: SearchLinkProps[];
	autoSuggestionResults: AutoSuggestionsResults;
	recentSearchResults: SearchLinkProps[];
	resultCount: SearchResultCount;
	selectedInputFilters: SelectedInputFilter[];
};

/**
 * This state object stores the results generated/displayed for each result type.
 */
export const initialState: {
	showDropdown: boolean;
	dropdownLoading: boolean;
	activeQueryFilterId: string;
	prefetchedRecentViewsResults: undefined;
	prefetchedRecentSearchResults: never[];
	peopleAndTeamsResults: {
		results: never[];
		duration: number;
	};
	recentViewsResults: never[];
	autoSuggestionResults: {
		results: never[];
		duration: number;
	};
	recentSearchResults: never[];
	resultCount: {
		peopleAndTeams: number;
		autoSuggestion: number;
		recentSearch: number;
		recentViews: number;
	};
	selectedInputFilters: never[];
} = {
	showDropdown: false,
	dropdownLoading: true,
	activeQueryFilterId: '',
	prefetchedRecentViewsResults: undefined,
	prefetchedRecentSearchResults: [],
	peopleAndTeamsResults: {
		results: [],
		duration: -1,
	},
	recentViewsResults: [],
	autoSuggestionResults: {
		results: [],
		duration: -1,
	},
	recentSearchResults: [],
	resultCount: {
		peopleAndTeams: 0,
		autoSuggestion: 0,
		recentSearch: 0,
		recentViews: 0,
	},
	selectedInputFilters: [],
};

const actions = {
	setShowDropdown:
		(showDropdown: boolean): Action<DropdownState> =>
		({ setState }) => {
			setState({ showDropdown });
		},
	setDropdownLoading:
		(dropdownLoading: boolean): Action<DropdownState> =>
		({ setState }) => {
			setState({ dropdownLoading });
		},
	setActiveQueryFilterId:
		(activeQueryFilterId: string): Action<DropdownState> =>
		({ setState }) => {
			setState({ activeQueryFilterId });
		},
	setPeopleAndTeamsResults:
		(peopleAndTeamsResults: PeopleAndTeamsResults): Action<DropdownState> =>
		({ setState }) => {
			setState({ peopleAndTeamsResults });
		},
	setRecentViewsResults:
		(recentViewsResults: SearchLinkProps[]): Action<DropdownState> =>
		({ setState }) => {
			setState({ recentViewsResults });
		},
	setAutoSuggestionResults:
		(autoSuggestionResults: AutoSuggestionsResults): Action<DropdownState> =>
		({ setState }) => {
			setState({ autoSuggestionResults });
		},
	setRecentSearchResults:
		(recentSearchResults: SearchLinkProps[]): Action<DropdownState> =>
		({ setState }) => {
			setState({ recentSearchResults });
		},
	setResultCount:
		(resultCount: SearchResultCount): Action<DropdownState> =>
		({ setState }) => {
			setState({ resultCount });
		},
	setPrefetchedRecentViewsResults:
		(prefetchedRecentViewsResults: SearchLinkProps[]): Action<DropdownState> =>
		({ setState }) => {
			setState({ prefetchedRecentViewsResults });
		},
	setPrefetchedRecentSearchResults:
		(prefetchedRecentSearchResults: SearchLinkProps[]): Action<DropdownState> =>
		({ setState }) => {
			setState({ prefetchedRecentSearchResults });
		},
	setSelectedInputFilters:
		(selectedInputFilters: SelectedInputFilter[]): Action<DropdownState> =>
		({ setState }) => {
			setState({ selectedInputFilters });
		},
};

type TActions = typeof actions;

// Keep store private and use hooks to access state and actions
const Store = createStore<DropdownState, TActions>({
	initialState,
	actions,
	name: 'dropdown',
});

// Hooks

export const useDropdownActions: import('react-sweet-state').HookActionsFunction<
	import('react-sweet-state').BoundActions<
		DropdownState,
		{
			setShowDropdown: (showDropdown: boolean) => Action<DropdownState>;
			setDropdownLoading: (dropdownLoading: boolean) => Action<DropdownState>;
			setActiveQueryFilterId: (activeQueryFilterId: string) => Action<DropdownState>;
			setPeopleAndTeamsResults: (
				peopleAndTeamsResults: PeopleAndTeamsResults,
			) => Action<DropdownState>;
			setRecentViewsResults: (recentViewsResults: SearchLinkProps[]) => Action<DropdownState>;
			setAutoSuggestionResults: (
				autoSuggestionResults: AutoSuggestionsResults,
			) => Action<DropdownState>;
			setRecentSearchResults: (recentSearchResults: SearchLinkProps[]) => Action<DropdownState>;
			setResultCount: (resultCount: SearchResultCount) => Action<DropdownState>;
			setPrefetchedRecentViewsResults: (
				prefetchedRecentViewsResults: SearchLinkProps[],
			) => Action<DropdownState>;
			setPrefetchedRecentSearchResults: (
				prefetchedRecentSearchResults: SearchLinkProps[],
			) => Action<DropdownState>;
			setSelectedInputFilters: (
				selectedInputFilters: SelectedInputFilter[],
			) => Action<DropdownState>;
		}
	>
> = createActionsHook(Store);

export const useDropdown: import('react-sweet-state').HookFunction<
	boolean,
	import('react-sweet-state').BoundActions<
		DropdownState,
		{
			setShowDropdown: (showDropdown: boolean) => Action<DropdownState>;
			setDropdownLoading: (dropdownLoading: boolean) => Action<DropdownState>;
			setActiveQueryFilterId: (activeQueryFilterId: string) => Action<DropdownState>;
			setPeopleAndTeamsResults: (
				peopleAndTeamsResults: PeopleAndTeamsResults,
			) => Action<DropdownState>;
			setRecentViewsResults: (recentViewsResults: SearchLinkProps[]) => Action<DropdownState>;
			setAutoSuggestionResults: (
				autoSuggestionResults: AutoSuggestionsResults,
			) => Action<DropdownState>;
			setRecentSearchResults: (recentSearchResults: SearchLinkProps[]) => Action<DropdownState>;
			setResultCount: (resultCount: SearchResultCount) => Action<DropdownState>;
			setPrefetchedRecentViewsResults: (
				prefetchedRecentViewsResults: SearchLinkProps[],
			) => Action<DropdownState>;
			setPrefetchedRecentSearchResults: (
				prefetchedRecentSearchResults: SearchLinkProps[],
			) => Action<DropdownState>;
			setSelectedInputFilters: (
				selectedInputFilters: SelectedInputFilter[],
			) => Action<DropdownState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.showDropdown,
});

export const useDropdownLoading: import('react-sweet-state').HookFunction<
	boolean,
	import('react-sweet-state').BoundActions<
		DropdownState,
		{
			setShowDropdown: (showDropdown: boolean) => Action<DropdownState>;
			setDropdownLoading: (dropdownLoading: boolean) => Action<DropdownState>;
			setActiveQueryFilterId: (activeQueryFilterId: string) => Action<DropdownState>;
			setPeopleAndTeamsResults: (
				peopleAndTeamsResults: PeopleAndTeamsResults,
			) => Action<DropdownState>;
			setRecentViewsResults: (recentViewsResults: SearchLinkProps[]) => Action<DropdownState>;
			setAutoSuggestionResults: (
				autoSuggestionResults: AutoSuggestionsResults,
			) => Action<DropdownState>;
			setRecentSearchResults: (recentSearchResults: SearchLinkProps[]) => Action<DropdownState>;
			setResultCount: (resultCount: SearchResultCount) => Action<DropdownState>;
			setPrefetchedRecentViewsResults: (
				prefetchedRecentViewsResults: SearchLinkProps[],
			) => Action<DropdownState>;
			setPrefetchedRecentSearchResults: (
				prefetchedRecentSearchResults: SearchLinkProps[],
			) => Action<DropdownState>;
			setSelectedInputFilters: (
				selectedInputFilters: SelectedInputFilter[],
			) => Action<DropdownState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.dropdownLoading,
});

export const useActiveQueryFilterId: import('react-sweet-state').HookFunction<
	string,
	import('react-sweet-state').BoundActions<
		DropdownState,
		{
			setShowDropdown: (showDropdown: boolean) => Action<DropdownState>;
			setDropdownLoading: (dropdownLoading: boolean) => Action<DropdownState>;
			setActiveQueryFilterId: (activeQueryFilterId: string) => Action<DropdownState>;
			setPeopleAndTeamsResults: (
				peopleAndTeamsResults: PeopleAndTeamsResults,
			) => Action<DropdownState>;
			setRecentViewsResults: (recentViewsResults: SearchLinkProps[]) => Action<DropdownState>;
			setAutoSuggestionResults: (
				autoSuggestionResults: AutoSuggestionsResults,
			) => Action<DropdownState>;
			setRecentSearchResults: (recentSearchResults: SearchLinkProps[]) => Action<DropdownState>;
			setResultCount: (resultCount: SearchResultCount) => Action<DropdownState>;
			setPrefetchedRecentViewsResults: (
				prefetchedRecentViewsResults: SearchLinkProps[],
			) => Action<DropdownState>;
			setPrefetchedRecentSearchResults: (
				prefetchedRecentSearchResults: SearchLinkProps[],
			) => Action<DropdownState>;
			setSelectedInputFilters: (
				selectedInputFilters: SelectedInputFilter[],
			) => Action<DropdownState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.activeQueryFilterId,
});

export const usePeopleAndTeamsResults: import('react-sweet-state').HookFunction<
	PeopleAndTeamsResults,
	import('react-sweet-state').BoundActions<
		DropdownState,
		{
			setShowDropdown: (showDropdown: boolean) => Action<DropdownState>;
			setDropdownLoading: (dropdownLoading: boolean) => Action<DropdownState>;
			setActiveQueryFilterId: (activeQueryFilterId: string) => Action<DropdownState>;
			setPeopleAndTeamsResults: (
				peopleAndTeamsResults: PeopleAndTeamsResults,
			) => Action<DropdownState>;
			setRecentViewsResults: (recentViewsResults: SearchLinkProps[]) => Action<DropdownState>;
			setAutoSuggestionResults: (
				autoSuggestionResults: AutoSuggestionsResults,
			) => Action<DropdownState>;
			setRecentSearchResults: (recentSearchResults: SearchLinkProps[]) => Action<DropdownState>;
			setResultCount: (resultCount: SearchResultCount) => Action<DropdownState>;
			setPrefetchedRecentViewsResults: (
				prefetchedRecentViewsResults: SearchLinkProps[],
			) => Action<DropdownState>;
			setPrefetchedRecentSearchResults: (
				prefetchedRecentSearchResults: SearchLinkProps[],
			) => Action<DropdownState>;
			setSelectedInputFilters: (
				selectedInputFilters: SelectedInputFilter[],
			) => Action<DropdownState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.peopleAndTeamsResults,
});

export const useRecentViewsResults: import('react-sweet-state').HookFunction<
	SearchLinkProps[],
	import('react-sweet-state').BoundActions<
		DropdownState,
		{
			setShowDropdown: (showDropdown: boolean) => Action<DropdownState>;
			setDropdownLoading: (dropdownLoading: boolean) => Action<DropdownState>;
			setActiveQueryFilterId: (activeQueryFilterId: string) => Action<DropdownState>;
			setPeopleAndTeamsResults: (
				peopleAndTeamsResults: PeopleAndTeamsResults,
			) => Action<DropdownState>;
			setRecentViewsResults: (recentViewsResults: SearchLinkProps[]) => Action<DropdownState>;
			setAutoSuggestionResults: (
				autoSuggestionResults: AutoSuggestionsResults,
			) => Action<DropdownState>;
			setRecentSearchResults: (recentSearchResults: SearchLinkProps[]) => Action<DropdownState>;
			setResultCount: (resultCount: SearchResultCount) => Action<DropdownState>;
			setPrefetchedRecentViewsResults: (
				prefetchedRecentViewsResults: SearchLinkProps[],
			) => Action<DropdownState>;
			setPrefetchedRecentSearchResults: (
				prefetchedRecentSearchResults: SearchLinkProps[],
			) => Action<DropdownState>;
			setSelectedInputFilters: (
				selectedInputFilters: SelectedInputFilter[],
			) => Action<DropdownState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.recentViewsResults,
});

export const useAutoSuggestionResults: import('react-sweet-state').HookFunction<
	AutoSuggestionsResults,
	import('react-sweet-state').BoundActions<
		DropdownState,
		{
			setShowDropdown: (showDropdown: boolean) => Action<DropdownState>;
			setDropdownLoading: (dropdownLoading: boolean) => Action<DropdownState>;
			setActiveQueryFilterId: (activeQueryFilterId: string) => Action<DropdownState>;
			setPeopleAndTeamsResults: (
				peopleAndTeamsResults: PeopleAndTeamsResults,
			) => Action<DropdownState>;
			setRecentViewsResults: (recentViewsResults: SearchLinkProps[]) => Action<DropdownState>;
			setAutoSuggestionResults: (
				autoSuggestionResults: AutoSuggestionsResults,
			) => Action<DropdownState>;
			setRecentSearchResults: (recentSearchResults: SearchLinkProps[]) => Action<DropdownState>;
			setResultCount: (resultCount: SearchResultCount) => Action<DropdownState>;
			setPrefetchedRecentViewsResults: (
				prefetchedRecentViewsResults: SearchLinkProps[],
			) => Action<DropdownState>;
			setPrefetchedRecentSearchResults: (
				prefetchedRecentSearchResults: SearchLinkProps[],
			) => Action<DropdownState>;
			setSelectedInputFilters: (
				selectedInputFilters: SelectedInputFilter[],
			) => Action<DropdownState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.autoSuggestionResults,
});

export const useRecentSearchResults: import('react-sweet-state').HookFunction<
	SearchLinkProps[],
	import('react-sweet-state').BoundActions<
		DropdownState,
		{
			setShowDropdown: (showDropdown: boolean) => Action<DropdownState>;
			setDropdownLoading: (dropdownLoading: boolean) => Action<DropdownState>;
			setActiveQueryFilterId: (activeQueryFilterId: string) => Action<DropdownState>;
			setPeopleAndTeamsResults: (
				peopleAndTeamsResults: PeopleAndTeamsResults,
			) => Action<DropdownState>;
			setRecentViewsResults: (recentViewsResults: SearchLinkProps[]) => Action<DropdownState>;
			setAutoSuggestionResults: (
				autoSuggestionResults: AutoSuggestionsResults,
			) => Action<DropdownState>;
			setRecentSearchResults: (recentSearchResults: SearchLinkProps[]) => Action<DropdownState>;
			setResultCount: (resultCount: SearchResultCount) => Action<DropdownState>;
			setPrefetchedRecentViewsResults: (
				prefetchedRecentViewsResults: SearchLinkProps[],
			) => Action<DropdownState>;
			setPrefetchedRecentSearchResults: (
				prefetchedRecentSearchResults: SearchLinkProps[],
			) => Action<DropdownState>;
			setSelectedInputFilters: (
				selectedInputFilters: SelectedInputFilter[],
			) => Action<DropdownState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.recentSearchResults,
});

export const useResultCount: import('react-sweet-state').HookFunction<
	SearchResultCount,
	import('react-sweet-state').BoundActions<
		DropdownState,
		{
			setShowDropdown: (showDropdown: boolean) => Action<DropdownState>;
			setDropdownLoading: (dropdownLoading: boolean) => Action<DropdownState>;
			setActiveQueryFilterId: (activeQueryFilterId: string) => Action<DropdownState>;
			setPeopleAndTeamsResults: (
				peopleAndTeamsResults: PeopleAndTeamsResults,
			) => Action<DropdownState>;
			setRecentViewsResults: (recentViewsResults: SearchLinkProps[]) => Action<DropdownState>;
			setAutoSuggestionResults: (
				autoSuggestionResults: AutoSuggestionsResults,
			) => Action<DropdownState>;
			setRecentSearchResults: (recentSearchResults: SearchLinkProps[]) => Action<DropdownState>;
			setResultCount: (resultCount: SearchResultCount) => Action<DropdownState>;
			setPrefetchedRecentViewsResults: (
				prefetchedRecentViewsResults: SearchLinkProps[],
			) => Action<DropdownState>;
			setPrefetchedRecentSearchResults: (
				prefetchedRecentSearchResults: SearchLinkProps[],
			) => Action<DropdownState>;
			setSelectedInputFilters: (
				selectedInputFilters: SelectedInputFilter[],
			) => Action<DropdownState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.resultCount,
});

export const usePrefetchedRecentViewsResults: import('react-sweet-state').HookFunction<
	SearchLinkProps[] | undefined,
	import('react-sweet-state').BoundActions<
		DropdownState,
		{
			setShowDropdown: (showDropdown: boolean) => Action<DropdownState>;
			setDropdownLoading: (dropdownLoading: boolean) => Action<DropdownState>;
			setActiveQueryFilterId: (activeQueryFilterId: string) => Action<DropdownState>;
			setPeopleAndTeamsResults: (
				peopleAndTeamsResults: PeopleAndTeamsResults,
			) => Action<DropdownState>;
			setRecentViewsResults: (recentViewsResults: SearchLinkProps[]) => Action<DropdownState>;
			setAutoSuggestionResults: (
				autoSuggestionResults: AutoSuggestionsResults,
			) => Action<DropdownState>;
			setRecentSearchResults: (recentSearchResults: SearchLinkProps[]) => Action<DropdownState>;
			setResultCount: (resultCount: SearchResultCount) => Action<DropdownState>;
			setPrefetchedRecentViewsResults: (
				prefetchedRecentViewsResults: SearchLinkProps[],
			) => Action<DropdownState>;
			setPrefetchedRecentSearchResults: (
				prefetchedRecentSearchResults: SearchLinkProps[],
			) => Action<DropdownState>;
			setSelectedInputFilters: (
				selectedInputFilters: SelectedInputFilter[],
			) => Action<DropdownState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.prefetchedRecentViewsResults,
});

export const usePrefetchedRecentSearchResults: import('react-sweet-state').HookFunction<
	SearchLinkProps[],
	import('react-sweet-state').BoundActions<
		DropdownState,
		{
			setShowDropdown: (showDropdown: boolean) => Action<DropdownState>;
			setDropdownLoading: (dropdownLoading: boolean) => Action<DropdownState>;
			setActiveQueryFilterId: (activeQueryFilterId: string) => Action<DropdownState>;
			setPeopleAndTeamsResults: (
				peopleAndTeamsResults: PeopleAndTeamsResults,
			) => Action<DropdownState>;
			setRecentViewsResults: (recentViewsResults: SearchLinkProps[]) => Action<DropdownState>;
			setAutoSuggestionResults: (
				autoSuggestionResults: AutoSuggestionsResults,
			) => Action<DropdownState>;
			setRecentSearchResults: (recentSearchResults: SearchLinkProps[]) => Action<DropdownState>;
			setResultCount: (resultCount: SearchResultCount) => Action<DropdownState>;
			setPrefetchedRecentViewsResults: (
				prefetchedRecentViewsResults: SearchLinkProps[],
			) => Action<DropdownState>;
			setPrefetchedRecentSearchResults: (
				prefetchedRecentSearchResults: SearchLinkProps[],
			) => Action<DropdownState>;
			setSelectedInputFilters: (
				selectedInputFilters: SelectedInputFilter[],
			) => Action<DropdownState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.prefetchedRecentSearchResults,
});

export const useSelectedInputFilters: import('react-sweet-state').HookFunction<
	SelectedInputFilter[],
	import('react-sweet-state').BoundActions<
		DropdownState,
		{
			setShowDropdown: (showDropdown: boolean) => Action<DropdownState>;
			setDropdownLoading: (dropdownLoading: boolean) => Action<DropdownState>;
			setActiveQueryFilterId: (activeQueryFilterId: string) => Action<DropdownState>;
			setPeopleAndTeamsResults: (
				peopleAndTeamsResults: PeopleAndTeamsResults,
			) => Action<DropdownState>;
			setRecentViewsResults: (recentViewsResults: SearchLinkProps[]) => Action<DropdownState>;
			setAutoSuggestionResults: (
				autoSuggestionResults: AutoSuggestionsResults,
			) => Action<DropdownState>;
			setRecentSearchResults: (recentSearchResults: SearchLinkProps[]) => Action<DropdownState>;
			setResultCount: (resultCount: SearchResultCount) => Action<DropdownState>;
			setPrefetchedRecentViewsResults: (
				prefetchedRecentViewsResults: SearchLinkProps[],
			) => Action<DropdownState>;
			setPrefetchedRecentSearchResults: (
				prefetchedRecentSearchResults: SearchLinkProps[],
			) => Action<DropdownState>;
			setSelectedInputFilters: (
				selectedInputFilters: SelectedInputFilter[],
			) => Action<DropdownState>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.selectedInputFilters,
});
